import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../../../../store/useAuthStore";
import Loading from "../../../../../Components/Loading";
import DialogBox from "../../../../../Components/Dialogbox";
import AddAccount from "../AddAccount";

const AccountHeader = ({
  accountName = "Unknown Account",
  accountType = "Unknown Type",
  ytp = "0",
  onEdit,
  onDelete,
}) => {
  return (
    <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm mb-6 flex justify-between items-center">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">{accountName}</h1>
        <p className="text-gray-500 mt-1">Type: {accountType}</p>
        <p className="text-gray-500">YTD: {ytp}</p>
      </div>
      <div className="space-x-3">
        <button
          onClick={onEdit}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Edit Account
        </button>
        <button
          onClick={onDelete}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Delete Account
        </button>
      </div>
    </div>
  );
};

const TransactionTable = ({
  accountName,
  accountType,
  setYtpValue,
  accountCode,
}) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);

        const subheaderValue = `${accountCode}-${accountName}`;
        const headerValue = accountType;

        const commonParams = {
          header: headerValue,
          subheader: subheaderValue,
          limit: -1,
        };

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [expenseResponse, incomeResponse] = await Promise.all([
          axios.get("/api/getallExpense", { headers, params: commonParams }),
          axios.get("/api/getallIncomes", { headers, params: commonParams }),
        ]);

        const expensesData = expenseResponse.data?.expenses || [];
        const incomesData = incomeResponse.data?.incomes || [];

        const allExpenses = expensesData.map((expense) => ({
          ...expense,
          debit: expense.amount || 0,
          credit: 0,
        }));
        const allIncomes = incomesData.map((income) => ({
          ...income,
          debit: 0,
          credit: income.amount || 0,
        }));

        const allTransactions = [...allExpenses, ...allIncomes].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        const totalDebit = allTransactions.reduce((sum, txn) => sum + txn.debit, 0);
        const totalCredit = allTransactions.reduce((sum, txn) => sum + txn.credit, 0);
        const ytp =
          totalDebit > totalCredit
            ? `${(totalDebit - totalCredit).toFixed(2)} Dr`
            : `${(totalCredit - totalDebit).toFixed(2)} Cr`;

        setYtpValue(ytp);
        setTotalCount(allTransactions.length);
        setTransactions(allTransactions);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to fetch transactions.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [accountName, accountType, token, setYtpValue, accountCode]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  if (loading) {
    return <Loading />;
  }
  if (error) return <p>{error}</p>;

  const paginatedTransactions = transactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="transactions table">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((txn, idx) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={txn._id || idx}
              >
                <TableCell>
                  {new Date(txn.date).toLocaleDateString()}
                </TableCell>
                <TableCell>{txn.description}</TableCell>
                <TableCell align="right">{txn.debit.toFixed(2)}</TableCell>
                <TableCell align="right">{txn.credit.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const TransactionRecordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const {
    accountName = "Unknown Account",
    accountType = "Unknown Type",
    accountCode = "Unknown Accountcode",
    accountId,
    accountTaxRate,
  } = location.state || {};
  const [ytpValue, setYtpValue] = useState("0");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!accountId) {
      setErrorMsg("Account ID not available.");
      setShowDeleteConfirm(false);
      return;
    }
    try {
      const response = await fetch("/api/deleteAccount", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: [accountId] }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || "Failed to delete account");
      }

      navigate("/ChartsOfAccount");
    } catch (err) {
      console.error("Error deleting account:", err.message);
      setErrorMsg(err.message || "An error occurred while deleting the account");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <button
        onClick={() => navigate("/ChartsOfAccount")}
        className="flex items-center mb-4 text-blue-500 hover:underline"
      >
        &larr; Back to Charts of Account
      </button>

      {errorMsg && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {errorMsg}
        </div>
      )}

      <AccountHeader
        accountName={accountName}
        accountType={accountType}
        ytp={ytpValue}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <TransactionTable
        accountName={accountName}
        accountType={accountType}
        setYtpValue={setYtpValue}
        accountCode={accountCode}
      />

      {/* Edit Account Modal */}
      {showEditModal && (
        <DialogBox
          title="Edit Account"
          description=""
          isVisible={showEditModal}
          onClose={() => setShowEditModal(false)}
        >
          <AddAccount
            accountDataToEdit={{
              _id: accountId,
              name: accountName,
              type: accountType,
              code: accountCode,
              taxRate: accountTaxRate,
            }}
            onAddAccount={() => setShowEditModal(false)}
            onAddSuccess={() => {
              setShowEditModal(false);
              navigate("/ChartsOfAccount");
            }}
            isEditing={true}
          />
        </DialogBox>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-2">Delete Account</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{accountName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionRecordPage;
